import { forwardRef } from 'react';
import { cx } from '@/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  'flex min-w-fit w-fit cursor-pointer items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-3xl border-2 px-[9px] py-0 text-center font-workSans text-base font-medium not-italic transition-all duration-300 ease-out disabled:pointer-events-none disabled:border-concrete disabled:bg-concrete disabled:text-silver',
  {
    variants: {
      color: {
        primary:
          'border-primary bg-primary text-primary hover:border-primaryDark hover:bg-primaryDark',
        elfGreen:
          'border-elfGreen bg-elfGreen text-elfGreen hover:border-greenDark hover:bg-greenDark',
        danger: 'border-redDark bg-redDark text-redDark hover:border-shiraz hover:bg-shiraz',
        white: 'bg-primary text-white',
        secondary:
          'border-transparent text-primaryDark hover:border-primary hover:bg-primary hover:text-white',
        silver:
          'border-silver text-dimGray hover:bg-hawkesBlue hover:border-white hover:!text-dimGray',
        blue: 'bg-marine text-white hover:bg-blueDark border-0 disabled:bg-concrete disabled:text-silver',
      },
      variant: {
        default: 'text-white',
        outline:
          'bg-transparent hover:text-white disabled:border-dimGray disabled:bg-transparent disabled:text-dimGray',
        ghost: 'border-none bg-transparent hover:bg-transparent disabled:bg-hawkesBlue',
      },
      size: { default: 'h-[42px]', icon: 'h-8 w-8', small: 'h-[36px]', sm: 'h-9 rounded-md px-3' },
    },
    compoundVariants: [
      {
        variant: 'ghost',
        color: 'primary',
        className: 'hover:text-primaryDark disabled:text-primaryDark',
      },
      {
        variant: 'ghost',
        color: 'elfGreen',
        className: 'hover:text-greenDark disabled:text-greenDark',
      },
      { variant: 'ghost', color: 'danger', className: 'hover:text-shiraz disabled:text-shiraz' },
    ],
    defaultVariants: { variant: 'default', color: 'primary', size: 'default' },
  }
);
type WithIcon = { icon: React.ElementType; iconPlacement: 'left' | 'right' };

type WithoutIcon = { icon?: never; iconPlacement?: undefined };

export type IconProps = WithIcon | WithoutIcon;

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {}

export type ButtonPropsWithIcon = ButtonProps & IconProps;

const Button = forwardRef<HTMLButtonElement, ButtonPropsWithIcon>(
  ({ className, variant, color, size, icon: Icon, iconPlacement = 'left', ...props }, ref) => {
    return (
      <button
        className={cx(buttonVariants({ color: color as any, variant, size, className }))}
        ref={ref}
        {...props}
      >
        {Icon && iconPlacement == 'left' && <span>{<Icon />}</span>}
        {props.children} {/* Renderizar el texto del botón */}
        {Icon && iconPlacement == 'right' && <span>{<Icon />}</span>}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
